<template>
  <div class="finish">
    <section v-for="(area, i) in value?.areas ?? []" :key="i" class="finish__area">
      <div class="finish__area-title">{{ area.name }}</div>

      <div v-for="(task, e) in area.tasks" :key="e" class="finish__task">
        <div class="finish__task-head">
          <div class="finish__task-name">{{ task.name }}</div>
          <div class="finish__status">
            <v-btn
              small
              depressed
              outlined
              class="finish__status-btn"
              :class="{ 'finish__status-btn--ok': task.ok === true }"
              @click="setOk(i, e, true)"
            >
              <v-icon small left>mdi-check</v-icon>
              {{ $t("inspections.ok") }}
            </v-btn>
            <v-btn
              small
              depressed
              outlined
              class="finish__status-btn"
              :class="{ 'finish__status-btn--not-ok': task.ok === false }"
              @click="setOk(i, e, false)"
            >
              <v-icon small left>mdi-close</v-icon>
              {{ $t("inspections.notOk") }}
            </v-btn>
          </div>
        </div>

        <v-textarea
          v-model="task.description"
          data-cy="content"
          :label="$t('inspections.comment')"
          outlined
          dense
          hide-details
          auto-grow
          rows="1"
          class="finish__comment"
          @change="updateInspection(i, e)"
        />

        <div class="finish__task-foot">
          <div class="finish__proof">
            <v-btn small outlined color="primary" @click="pickProof(i, e)">
              <v-icon small left>mdi-paperclip</v-icon>
              {{ $t("inspections.addProof") }}
            </v-btn>
            <input
              :ref="`proof-${i}-${e}`"
              type="file"
              accept="image/*"
              class="d-none"
              @change="onProofPicked($event, i, e)"
            />
            <div v-if="task.proof && task.proof.name" class="finish__proof-file">
              <v-icon small class="mr-1">mdi-image-outline</v-icon>
              <span class="finish__proof-name">{{ task.proof.name }}</span>
              <v-btn icon x-small class="ml-1" @click="handleFile(null, i, e)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="finish__actions">
            <v-chip
              small
              outlined
              :color="riskColor(riskOf(task))"
              class="finish__risk"
              @click="openRiskDialog(i, e)"
            >
              <v-icon small left>mdi-alert-circle-outline</v-icon>
              {{ $t("riskAssessment.risks.risk") }} {{ riskOf(task) }}
            </v-chip>
            <v-btn
              v-if="canCreateTickets || !!task.ticket"
              small
              outlined
              color="primary"
              :disabled="!!task.ticket && !canUpdateTicket(i, e)"
              @click="createTicket(task, i, e)"
            >
              <template v-if="task.ticket == null">
                <v-icon small left>mdi-plus</v-icon>
                {{ $t("inspections.createTicket") }}
              </template>
              <template v-else-if="canUpdateTicket(i, e)">
                <v-icon small left>mdi-sync</v-icon>
                {{ $t("inspections.updateTicket") }}
              </template>
              <template v-else>
                <v-icon small left>mdi-check</v-icon>
                {{ $t("inspections.ticketCreated") }}
              </template>
            </v-btn>
          </div>
        </div>
      </div>

      <div class="finish__add">
        <template v-if="addingArea === i">
          <v-text-field
            ref="newTaskInput"
            v-model="newTaskName"
            :placeholder="$t('inspections.newTaskName')"
            outlined
            dense
            hide-details
            autofocus
            class="finish__add-input"
            @keydown.enter.prevent="confirmAddTask(i)"
            @keydown.esc.prevent="cancelAddTask"
          />
          <v-btn small depressed color="primary" :disabled="!newTaskName.trim()" @click="confirmAddTask(i)">
            {{ $t("riskAssessment.advanced.addDescriptionPoint") }}
          </v-btn>
          <v-btn small text @click="cancelAddTask">{{ $t("general.cancel") }}</v-btn>
        </template>
        <v-btn v-else small text color="primary" @click="startAddTask(i)">
          <v-icon small left>mdi-plus</v-icon>
          {{ $t("inspections.addTask") }}
        </v-btn>
      </div>
    </section>
    <riskDialog
      :value="currentRisk"
      :dialogState="riskDialog"
      @input="setRisk"
    />
  </div>
</template>
<script>
import riskDialog from "../riskAssessment/riskNohlDialog.vue";

export default {
  props: ["value"],
  components: { riskDialog },
  data: () => ({
    addingArea: null,
    newTaskName: "",
    canUpdateTickets: {},
    riskDialog: { open: false },
    riskTarget: null,
  }),
  computed: {
    currentRisk() {
      if (!this.riskTarget) return 1;
      const task =
        this.value.areas[this.riskTarget.area].tasks[this.riskTarget.task];
      return this.riskOf(task);
    },
  },
  methods: {
    riskOf(task) {
      return Number((task && task.riskClassification) || 1);
    },
    riskColor(risk) {
      if (risk < 3) return "success";
      if (risk < 5) return "warning";
      return "error";
    },
    ticketPriority(task) {
      const risk = this.riskOf(task);
      if (risk < 3) return 1;
      if (risk < 5) return 2;
      return 3;
    },
    startAddTask(areaId) {
      this.addingArea = areaId;
      this.newTaskName = "";
    },
    cancelAddTask() {
      this.addingArea = null;
      this.newTaskName = "";
    },
    async confirmAddTask(areaId) {
      const name = this.newTaskName.trim();
      if (!name) return;
      this.value.areas[areaId].tasks.push({
        name,
        ok: true,
        riskClassification: 1,
        description: "",
        proof: null,
        ticket: null,
      });
      this.cancelAddTask();
      await this.updateInspection();
    },
    setOk(areaId, taskId, ok) {
      this.$set(this.value.areas[areaId].tasks[taskId], "ok", ok);
    },
    pickProof(areaId, taskId) {
      const ref = this.$refs[`proof-${areaId}-${taskId}`];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) input.click();
    },
    onProofPicked(event, areaId, taskId) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (file) this.handleFile(file, areaId, taskId);
    },
    openRiskDialog(areaId, taskId) {
      this.riskTarget = { area: areaId, task: taskId };
      this.riskDialog.open = true;
    },
    setRisk(risk) {
      if (!this.riskTarget) return;
      const { area, task } = this.riskTarget;
      this.$set(this.value.areas[area].tasks[task], "riskClassification", risk);
      this.updateInspection(area, task);
    },
    setCanUpdateTicket(i, e, value) {
      this.canUpdateTickets[`${i},${e}`] = value;

      this.$forceUpdate();
    },
    canUpdateTicket(i, e) {
      return this.canUpdateTickets[`${i},${e}`];
    },
    updateInspection(areaId, taskId) {
      if (typeof areaId === "number" && typeof taskId === "number") {
        this.setCanUpdateTicket(areaId, taskId, true);
      }
      return this.$store.dispatch("updateInspection", {
        ...this.value,
        id: this.value._id,
      });
    },
    async createTicket(task, areaId, taskId) {
      try {
        const item = await this.$store.dispatch(
          "getSettingData",
          "standardOpenBoard"
        );
        if (task.ticket && this.canUpdateTicket(areaId, taskId)) {
          let ticketData = {
            title: task.name,
            description: task.description,
            board: item.value,
            priority: this.ticketPriority(task),
            _id: task.ticket._id,
          };
          const r = await this.$store.dispatch("updateTicket", ticketData);
          if (
            !task.ticket.attachments.find(
              (a) => a.location === task.proof.location
            )
          ) {
            await this.$store.dispatch("uploadAttachmentToTicketFromTask", {
              id: this.value.id,
              ticketId: r._id,
              ...task.proof,
            });
          }
        } else {
          let ticketData = {
            recurring: 0,
            title: task.name,
            description: task.description,
            dueDate: null,
            users: [],
            priority: this.ticketPriority(task),
            board: item.value,
          };
          const r = await this.$store.dispatch("addTicket", ticketData);
          this.value.areas[areaId].tasks[taskId].ticket = r;

          if (task.proof) {
            this.value.areas[areaId].tasks[taskId].ticket =
              await this.$store.dispatch("uploadAttachmentToTicketFromTask", {
                id: this.value.id,
                ticketId: r._id,
                ...task.proof,
              });
          }

          await this.updateInspection();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setCanUpdateTicket(areaId, taskId, false);
      }
    },
    async handleFile(file, areaId, taskId) {
      if (!file) {
        this.value.areas[areaId].tasks[taskId].proof = null;
      } else {
        try {
          const fileData = await this.createBase64(file);
          this.value.areas[areaId].tasks[taskId].proof = fileData;
        } catch (error) {
          console.log(error);
        }
      }
      const updatedInspection = await this.updateInspection(areaId, taskId);
      this.value.areas[areaId].tasks[taskId].proof =
        updatedInspection.areas[areaId].tasks[taskId].proof;
    },
    async createBase64(fileObject) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            filetype: fileObject.name.match(/\.[0-9a-z]+$/i)[0].split(".")[1],
            name: fileObject.name,
            content: e.target.result.split(",")[1],
          });
        };
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(fileObject);
      });
    },
  },
};
</script>

<style scoped>
.finish__area {
  margin-top: 16px;
}
.finish__area-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 10px;
}
.finish__task {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.finish__task-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.finish__task-name {
  font-weight: 600;
  font-size: 0.95rem;
  padding-top: 4px;
}
.finish__status {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.finish__status-btn {
  text-transform: none;
  letter-spacing: normal;
  min-width: 140px !important;
  color: rgba(0, 0, 0, 0.6) !important;
  border-color: rgba(0, 0, 0, 0.2) !important;
}
.finish__status-btn--ok {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
  border-color: #66bb6a !important;
}
.finish__status-btn--not-ok {
  background: #ffebee !important;
  color: #c62828 !important;
  border-color: #ef5350 !important;
}
.finish__comment {
  margin-bottom: 12px;
}
.finish__task-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.finish__proof {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.finish__proof-file {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  min-width: 0;
}
.finish__proof-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.finish__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.finish__add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -4px 0 8px;
}
.finish__add-input {
  max-width: 420px;
}
.finish__add .v-btn {
  text-transform: none;
  letter-spacing: normal;
}
.finish__task .v-btn {
  text-transform: none;
  letter-spacing: normal;
}
@media (max-width: 600px) {
  .finish__task-head {
    flex-direction: column;
  }
  .finish__status-btn {
    min-width: 0 !important;
  }
}
</style>
